import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LoadingSpinner from './LoadingSpinner';
import Alert from './Alert';

const TextSubstitution = () => {
  const [texts, setTexts] = useState([]);
  const [filteredTexts, setFilteredTexts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTextId, setSelectedTextId] = useState('');
  const [selectedText, setSelectedText] = useState(null);
  const [variables, setVariables] = useState({});
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    fetchTexts();
  }, []);

  // Filtra os textos sempre que o termo de busca ou a lista de textos mudar
  useEffect(() => {
    if (texts.length > 0) {
      if (!searchTerm.trim()) {
        setFilteredTexts(texts);
      } else {
        const filtered = texts.filter(text => 
          text.texto_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          text.conteudo.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredTexts(filtered);
      }
    }
  }, [searchTerm, texts]);

  // Efeito para processar os parâmetros da URL quando os textos são carregados
  useEffect(() => {
    if (texts.length > 0) {
      // Obter o ID do texto da URL, se existir
      const searchParams = new URLSearchParams(location.search);
      const textoIdFromUrl = searchParams.get('textoId');
      
      if (textoIdFromUrl) {
        console.log('Texto ID da URL:', textoIdFromUrl);
        const textFromUrl = texts.find(t => t.id.toString() === textoIdFromUrl.toString());
        
        if (textFromUrl) {
          setSelectedTextId(textoIdFromUrl.toString());
          setSelectedText(textFromUrl);
          
          // Inicializar as variáveis
          const newVariables = {};
          textFromUrl.variaveis.forEach(v => {
            newVariables[v] = '';
          });
          setVariables(newVariables);
        } else {
          console.warn(`Texto com ID ${textoIdFromUrl} não encontrado`);
        }
      }
    }
  }, [texts, location.search]);

  const fetchTexts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/textos');
      setTexts(response.data);
      setFilteredTexts(response.data);
    } catch (error) {
      console.error('Erro ao buscar textos:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        navigate('/login');
      }
      setAlert({ message: 'Erro ao carregar textos. Tente novamente.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateResult = useCallback(() => {
    if (selectedText) {
      let substitutedText = selectedText.conteudo;
      Object.entries(variables).forEach(([variable, value]) => {
        const regex = new RegExp(`@${variable}`, 'g');
        substitutedText = substitutedText.replace(regex, value || `@${variable}`);
      });
      setResult(substitutedText);
    }
  }, [selectedText, variables]);

  useEffect(() => {
    updateResult();
  }, [variables, selectedText, updateResult]);

  const handleTextSelect = (textId) => {
    if (textId) {
      // Atualizar a URL com o ID do texto selecionado
      navigate(`/substituir?textoId=${textId}`, { replace: true });
      
      const text = texts.find(t => t.id.toString() === textId.toString());
      setSelectedTextId(textId.toString());
      setSelectedText(text);
      setSearchTerm(''); // Limpar o campo de busca ao selecionar um texto
      
      if (text) {
        const newVariables = {};
        text.variaveis.forEach(v => {
          newVariables[v] = '';
        });
        setVariables(newVariables);
        
        // Registrar visualização do texto
        try {
          api.post('/registrar-copia', { 
            texto_id: text.id,
            acao: 'visualizacao'
          }).catch(err => {
            console.log('Erro ao registrar visualização:', err);
          });
        } catch (err) {
          console.log('Erro ao registrar visualização:', err);
        }
      }
    } else {
      // Limpar parâmetro da URL se nenhum texto for selecionado
      navigate('/substituir', { replace: true });
      setSelectedTextId('');
      setSelectedText(null);
      setVariables({});
    }
    
    setResult('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleVariableChange = (variable, value) => {
    setVariables(prev => ({...prev, [variable]: value}));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result).then(() => {
      setAlert({ message: 'Texto copiado com sucesso!', type: 'success' });
    }, (err) => {
      console.error('Erro ao copiar texto: ', err);
      setAlert({ message: 'Erro ao copiar texto. Tente novamente.', type: 'error' });
    });
  };

  const highlightVariables = (text) => {
    let highlightedText = text;
    Object.keys(variables).forEach(variable => {
      const regex = new RegExp(`@${variable}`, 'g');
      highlightedText = highlightedText.replace(regex, `<span class="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300 px-1 rounded">@${variable}</span>`);
    });
    return <div dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setAlert({
        type: 'success',
        message: 'Texto copiado para a área de transferência!'
      });
      
      // Registrar a cópia como um uso adicional
      try {
        await api.post('/registrar-copia', { 
          texto_id: selectedText.id,
          acao: 'copia'
        });
      } catch (err) {
        console.log('Erro ao registrar cópia:', err);
      }
      
      setTimeout(() => {
        setAlert(null);
      }, 3000);
    } catch (err) {
      setAlert({
        type: 'error',
        message: 'Erro ao copiar texto. Tente copiar manualmente.'
      });
      setTimeout(() => {
        setAlert(null);
      }, 3000);
    }
  };

  // Iniciar o relógio quando um texto é selecionado
  useEffect(() => {
    if (selectedText) {
      setStartTime(Date.now());
    }
    
    // Função para enviar o tempo da sessão quando o usuário sair da página
    return () => {
      if (selectedText && startTime) {
        const totalSeconds = Math.floor((Date.now() - startTime) / 1000);
        // Registra o tempo de permanência apenas se for significativo (mais de 5 segundos)
        if (totalSeconds > 5) {
          api.post('/registrar-sessao', {
            texto_id: selectedText.id,
            tempo_segundos: totalSeconds
          }).catch(err => console.log('Erro ao registrar tempo:', err));
        }
      }
    };
  }, [selectedText, startTime]);

  if (loading) {
    return <div className="flex justify-center items-center h-64"><LoadingSpinner /></div>;
  }

  return (
    <div className="w-full">
      {alert && <Alert message={alert.message} type={alert.type} />}
      
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6">
        <label htmlFor="search-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Selecione um texto para substituir as variáveis
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
          <input
            id="search-input"
            type="text"
            placeholder="Digite para encontrar textos..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 block w-full rounded-md border-gray-300 dark:border-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {!selectedText && (
          <div className="mt-4 max-h-60 overflow-y-auto">
            {filteredTexts.length === 0 ? (
              <p className="py-4 text-center text-gray-500 dark:text-gray-400">
                Nenhum texto encontrado.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTexts.map((text) => (
                  <li 
                    key={text.id} 
                    onClick={() => handleTextSelect(text.id)}
                    className="py-3 px-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 rounded-md"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {text.texto_id}
                      </span>
                      <svg 
                        className="h-5 w-5 text-gray-400" 
                        xmlns="http://www.w3.org/2000/svg" 
                        viewBox="0 0 20 20" 
                        fill="currentColor"
                      >
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    {searchTerm && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {text.conteudo.substring(0, 150)}...
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {selectedText && (
          <div className="mt-3 flex items-center justify-between py-2 px-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
            <div>
              <span className="font-medium text-gray-900 dark:text-white">
                Texto selecionado: {selectedText.texto_id}
              </span>
            </div>
            <button
              onClick={() => handleTextSelect(null)}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
            >
              Alterar
            </button>
          </div>
        )}
      </div>
      
      <AnimatePresence>
        {selectedText && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Coluna esquerda - Entrada de dados */}
            <div className="space-y-6">
              <motion.div 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Texto original:</h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-md text-gray-700 dark:text-gray-300 whitespace-pre-wrap border border-gray-200 dark:border-gray-700">
                  {highlightVariables(selectedText.conteudo)}
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Variáveis:</h3>
                <div className="space-y-4">
                  {Object.entries(variables).map(([variable, value], index) => (
                    <motion.div
                      key={variable}
                      initial={{ x: -10, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.1 * index + 0.2 }}
                    >
                      <label htmlFor={variable} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        @{variable}:
                      </label>
                      <input 
                        type="text" 
                        id={variable}
                        value={value} 
                        onChange={(e) => handleVariableChange(variable, e.target.value)} 
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 dark:bg-gray-700 dark:text-white"
                        placeholder={`Digite o valor para @${variable}`}
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
            
            {/* Coluna direita - Resultado */}
            <div className="space-y-6">
              <motion.div 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6"
              >
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Resultado:</h3>
                  <motion.button
                    onClick={handleCopyClick}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                      <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                    </svg>
                    Copiar
                  </motion.button>
                </div>
                <div className="p-4 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-md whitespace-pre-wrap min-h-[300px] dark:text-white">
                  {result || (
                    <p className="text-gray-500 dark:text-gray-400 italic">O texto com as substituições aparecerá aqui...</p>
                  )}
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6"
              >
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Instruções:</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li>Digite no campo acima para encontrar um texto</li>
                  <li>Clique em um texto da lista para selecioná-lo</li>
                  <li>Preencha os valores para cada variável</li>
                  <li>O resultado será atualizado automaticamente</li>
                  <li>Clique em "Copiar" para copiar o texto final</li>
                </ul>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {!selectedText && !loading && filteredTexts.length === 0 && !searchTerm && (
        <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-gray-800 shadow-md rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg">Digite para encontrar um texto</p>
          <p className="text-gray-400 dark:text-gray-500">Você poderá substituir as variáveis e obter o texto final</p>
        </div>
      )}
    </div>
  );
};

export default TextSubstitution;